import { mcpServerListResponseSchema, mcpServerUsersResponseSchema, listMcpServersParamsSchema, mcpServerNameSchema, mcpFilterOptionsQuerySchema, mcpFilterOptionsResponseSchema, mcpTableResponseSchema, } from '../contracts/mcp';
import { listPersonalMcpServersDataTableParamsSchema, personalMcpFilterOptionsQuerySchema, personalMcpFilterOptionsResponseSchema, personalMcpTableResponseSchema, } from '../contracts/account-mcp';
import { serializeTableQueryParams } from '../contracts/table';
import { parseSchema } from '../errors';
import { organizationIdSchema, } from '../contracts/organizations';
/**
 * List MCP servers for an organization
 *
 * Authorization:
 * - Owner/Manager: Returns all MCP servers in the organization
 * - Member: Returns only the user's own MCP servers
 *
 * @param client - Axios client instance
 * @param organizationId - Organization ID
 * @param params - Query parameters for filtering and pagination
 * @returns Paginated list of MCP servers
 */
export async function listOrganizationMCPServers(client, organizationId, params = {}, options) {
    const validatedId = parseSchema(organizationIdSchema, organizationId);
    const validatedParams = parseSchema(listMcpServersParamsSchema, params);
    const response = await client.get(`/organizations/${validatedId}/mcp-servers`, {
        params: validatedParams,
        paramsSerializer: serializeTableQueryParams,
        signal: options?.signal,
    });
    return parseSchema(mcpTableResponseSchema, response);
}
/**
 * List personal MCP servers (for users without an organization)
 *
 * Returns MCP servers where organizationId is NULL, filtered by the authenticated user.
 * Uses the legacy response shape ({ servers, pagination }).
 *
 * @param client - Axios client instance
 * @param params - Query parameters for filtering and pagination
 * @returns Paginated list of personal MCP servers (legacy shape)
 */
export async function listUserMCPServers(client, params = {}) {
    const validatedParams = parseSchema(listMcpServersParamsSchema, params);
    const response = await client.get('/account/mcp-servers', {
        params: validatedParams,
    });
    return parseSchema(mcpServerListResponseSchema, response);
}
/**
 * Get users who have a specific MCP server configured
 *
 * Authorization:
 * - Owner/Manager: Returns all users with this MCP
 * - Member: Returns only the requesting user's data
 *
 * @param client - Axios client instance
 * @param organizationId - Organization ID
 * @param serverName - Row identity: the `.mcp.json` `config_name`. Param
 *                     kept for caller back-compat; sent as `configName`.
 * @returns List of users with this MCP configured
 */
export async function getMCPServerUsers(client, organizationId, serverName) {
    const validatedId = parseSchema(organizationIdSchema, organizationId);
    const validatedConfigName = parseSchema(mcpServerNameSchema, serverName);
    const response = await client.post(`/organizations/${validatedId}/mcp-servers/users`, { configName: validatedConfigName });
    return parseSchema(mcpServerUsersResponseSchema, response);
}
/**
 * List personal MCP servers with DataTable support (filters, search, sorting).
 *
 * Sends filters using bracket notation via serializeTableQueryParams.
 * The response follows the standard DataTable shape: { data, pagination, meta }.
 *
 * @param client - Axios client instance
 * @param params - DataTable query parameters (pagination, filters, sort, search)
 * @param options - Optional AbortSignal for request cancellation
 * @returns DataTable response with MCP server rows
 */
export async function listUserMCPServersDataTable(client, params = {}, options) {
    const validatedParams = parseSchema(listPersonalMcpServersDataTableParamsSchema, params);
    const response = await client.get('/account/mcp-servers/table', {
        params: validatedParams,
        paramsSerializer: serializeTableQueryParams,
        signal: options?.signal,
    });
    return parseSchema(personalMcpTableResponseSchema, response);
}
/**
 * Get filter option values for a specific MCP field (organization level).
 * Supports name, version, and publisher fields (dynamic options).
 *
 * @param client - Axios client instance
 * @param organizationId - Organization ID
 * @param params - Field, search, limit, offset
 * @returns Paginated list of option strings
 */
export async function getMCPFilterOptions(client, organizationId, params) {
    const validatedId = parseSchema(organizationIdSchema, organizationId);
    const validatedParams = parseSchema(mcpFilterOptionsQuerySchema, params);
    const response = await client.get(`/organizations/${validatedId}/mcp-servers/filter-options`, { params: validatedParams });
    return parseSchema(mcpFilterOptionsResponseSchema, response);
}
/**
 * Get distinct filter option values for a personal MCP servers field.
 *
 * @param client - Axios client instance
 * @param params - Field, optional search, limit, offset
 * @returns List of option values and pagination info
 */
export async function getUserMCPFilterOptions(client, params) {
    const validatedParams = parseSchema(personalMcpFilterOptionsQuerySchema, params);
    const response = await client.get('/account/mcp-servers/filter-options', {
        params: validatedParams,
    });
    return parseSchema(personalMcpFilterOptionsResponseSchema, response);
}
//# sourceMappingURL=mcp.js.map